import {
  useState,
  useCallback,
  useEffect,
  useRef,
  MutableRefObject,
  Dispatch,
  SetStateAction,
} from 'react';
import {
  IAssistantScript,
  PlaybackPolicy,
  ProgressEntry,
} from '@interfaces/assistantScript';
import { IAssistantRagVectorStorage } from '@shared/interfaces/assistantRagStorage';
import { LinearScriptPlayer } from './scriptPlayer/linearScriptPlayer';
import { ScriptNodeJSON } from './scriptPlayer/intefraces';
import { log } from './Debug';
import { globals } from './globals';
import {
  isLandingTrigger,
  isAITrigger,
  triggerMatchesRegion,
} from './providerUtils';
import { SCREENS } from '@core/assistant/interfaces';
import { StartFlowFn } from './useScriptFlow';

// Grace period between the no-script/no-knowledge auto-close decision and
// actually calling setOpen(false) — a quick walk back into a real trigger
// within this window (which clears the pending timer at the top of
// handleRegionChange) supersedes the close instead of the panel flickering
// shut and immediately reopening.
export const AUTO_CLOSE_GRACE_MS = 250;

export type UseRegionHandlerParams = {
  linearScriptPlayerRef: MutableRefObject<LinearScriptPlayer | undefined>;
  isAudioPlaybackActiveRef: MutableRefObject<boolean>;
  scriptsRef: MutableRefObject<IAssistantScript[]>;
  // Scoped RAG knowledge items (see useAssistantKnowledge) — read via a ref
  // for the same reason as isAskMeAnythingEnabledRef.
  ragItemsRef: MutableRefObject<IAssistantRagVectorStorage[]>;
  progressState: MutableRefObject<string>;
  sessionProgressRef: MutableRefObject<Record<string, ProgressEntry[]>>;
  enqueuedScriptIdRef: MutableRefObject<string | null>;
  stopPlaybackRef: MutableRefObject<() => void>;
  startFlowRef: MutableRefObject<StartFlowFn>;
  setOpen: (open: boolean) => void;
  // True when "Ask Me Anything" is enabled for the current event. Read via a ref
  // because handleRegionChange is a stable callback (empty dep array).
  isAskMeAnythingEnabledRef: MutableRefObject<boolean>;
  // The real "on join" region name (or the synthetic 'landing' fallback) —
  // read via a ref for the same reason as isAskMeAnythingEnabledRef.
  joinTriggerNameRef: MutableRefObject<string>;
  // Child region name -> parent region name, for regions with a parentTrigger
  // (e.g. exhibition booths grouped under an exhibition hall) — read via a
  // ref for the same reason as isAskMeAnythingEnabledRef.
  parentTriggerMapRef: MutableRefObject<Record<string, string>>;
  setActiveScreen: Dispatch<SetStateAction<SCREENS>>;
  setNavigationHistory: Dispatch<SetStateAction<SCREENS[]>>;
  setIsScriptCompleted: Dispatch<SetStateAction<boolean>>;
  setIsScriptTransitioning: Dispatch<SetStateAction<boolean>>;
  setIsScriptQuizActive: Dispatch<SetStateAction<boolean>>;
  setIsDisplayUserFeedback: Dispatch<SetStateAction<boolean>>;
  setCanUserSkip: Dispatch<SetStateAction<boolean>>;
  setPendingScriptStart: Dispatch<
    SetStateAction<{
      step: number;
      area: string;
      title: string;
      scriptId: string;
    } | null>
  >;
};

export const useRegionHandler = ({
  linearScriptPlayerRef,
  isAudioPlaybackActiveRef,
  scriptsRef,
  ragItemsRef,
  progressState,
  sessionProgressRef,
  enqueuedScriptIdRef,
  stopPlaybackRef,
  startFlowRef,
  setOpen,
  isAskMeAnythingEnabledRef,
  joinTriggerNameRef,
  parentTriggerMapRef,
  setActiveScreen,
  setNavigationHistory,
  setIsScriptCompleted,
  setIsScriptTransitioning,
  setIsScriptQuizActive,
  setIsDisplayUserFeedback,
  setCanUserSkip,
  setPendingScriptStart,
}: UseRegionHandlerParams) => {
  const [simulatedRegionOverride, setSimulatedRegionOverride] = useState<
    string | undefined
  >(undefined);

  const pendingAutoCloseTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  useEffect(() => {
    return () => {
      if (pendingAutoCloseTimeoutRef.current !== null) {
        clearTimeout(pendingAutoCloseTimeoutRef.current);
      }
    };
  }, []);

  // Exposed so a deliberate open (the assistant button, or any other direct
  // `setOpen(true)`) can cancel a close that a region change already
  // scheduled — without this, an auto-close timer armed before the open
  // (e.g. the mount-time region resolution into a script-less, knowledge-
  // less branding) would still fire afterwards and silently undo it, since
  // opening the panel doesn't itself go through handleRegionChange.
  const cancelPendingAutoClose = useCallback(() => {
    if (pendingAutoCloseTimeoutRef.current !== null) {
      clearTimeout(pendingAutoCloseTimeoutRef.current);
      pendingAutoCloseTimeoutRef.current = null;
    }
  }, []);

  const handleRegionChange = useCallback((incomingRegion: string) => {
    log('[region] changed to', incomingRegion);

    // Any new region change re-decides whether a pending auto-close (see the
    // no-script branch below) should still happen — scripted or not, this
    // call site re-evaluates from scratch.
    if (pendingAutoCloseTimeoutRef.current !== null) {
      clearTimeout(pendingAutoCloseTimeoutRef.current);
      pendingAutoCloseTimeoutRef.current = null;
    }

    const playerState = linearScriptPlayerRef.current?.getCurrentState();
    if (playerState?.isRunning && playerState.name === incomingRegion) {
      // Deliberately-started playback (e.g. Visited Areas "resume/replay/select
      // step", which teleports then calls startFlow with bypassPlaybackPolicy)
      // sets isRunning synchronously, before its audio fetch resolves. The
      // teleport's own UE region-enter echo can land in that window — without
      // this check it would be mistaken for a fresh walk-in and restart the
      // script from scratch (or, for ui_click, stop the in-flight fetch and
      // surface a redundant "Take a tour" prompt on top of it).
      log('[region] script already running for this region — skipping restart');
      return;
    }

    const findScriptForRegion = (regionName: string) =>
      scriptsRef.current.find((x) => {
        const triggers = Array.isArray(x.scriptTrigger)
          ? x.scriptTrigger
          : [x.scriptTrigger];
        return triggers.some((trigger) =>
          triggerMatchesRegion(trigger, regionName),
        );
      });

    // A script bound to a "linked area" is scoped to the PARENT region's name
    // ([parentName, ...spots]) — a child region (e.g. an exhibition booth) has
    // no trigger token of its own. Fall back to the parent's name so entering
    // any child still surfaces the parent's script.
    const parentRegionName = incomingRegion
      ? parentTriggerMapRef.current[incomingRegion]
      : undefined;
    const scriptData =
      findScriptForRegion(incomingRegion as string) ??
      (parentRegionName ? findScriptForRegion(parentRegionName) : undefined);

    log('[region] script found for region', {
      scriptId: scriptData?.scriptId ?? 'none',
      triggerType: scriptData?.triggerType,
      interruptMode: scriptData?.interruptMode,
      bridgeRegionGaps: scriptData?.bridgeRegionGaps ?? false,
      canUserSkip: scriptData?.canUserSkip,
    });

    const defaultPlaybackPolicy = isAITrigger(scriptData?.scriptTrigger ?? '')
      ? PlaybackPolicy.ALWAYS
      : PlaybackPolicy.ONCE_SESSION;
    const effectivePlaybackPolicy =
      scriptData?.playbackPolicy ?? defaultPlaybackPolicy;

    if (scriptData) {
      if (!scriptData.interruptMode) {
        scriptData.interruptMode = 'finish_step';
      }

      const parsedNodes: ScriptNodeJSON[] = JSON.parse(
        scriptData.scriptData || '[]',
      );
      const lastNodeIndex = parsedNodes.length - 1;
      const isCompletedIn = (state: Record<string, ProgressEntry[]>) => {
        const entries = state[incomingRegion];
        return (
          parsedNodes.length > 0 &&
          !!entries?.length &&
          (entries.at(-1)?.[0] ?? 0) >= lastNodeIndex
        );
      };

      const isCompleted =
        effectivePlaybackPolicy === PlaybackPolicy.ONCE_SESSION ||
        effectivePlaybackPolicy === PlaybackPolicy.FIRST_TIME
          ? isCompletedIn(sessionProgressRef.current)
          : isCompletedIn(JSON.parse(progressState.current || '{}'));

      if (isCompleted && effectivePlaybackPolicy !== PlaybackPolicy.ALWAYS) {
        const currentPlayerState =
          linearScriptPlayerRef.current?.getCurrentState();
        const protectingDifferentRegion =
          (currentPlayerState?.isRunning || isAudioPlaybackActiveRef.current) &&
          currentPlayerState?.name !== incomingRegion;
        if (protectingDifferentRegion) {
          log(
            '[region] script already completed — updating region silently, not stopping audio',
            incomingRegion,
          );
          globals.currentRegion = incomingRegion;
          return;
        }
        log(
          '[region] script already completed — fall through to normal flow',
          incomingRegion,
        );
      }
    }

    if (!scriptData) {
      log(
        '[region] no script for region — stopping current player',
        incomingRegion,
      );

      const currentPlayerName =
        linearScriptPlayerRef.current?.getCurrentState().name;

      // Only suppress the reset for the synthetic 'landing' trigger, which is
      // never a real UE region — an empty incoming region while it plays just
      // means region-tracking hasn't caught up yet, not that the user left.
      // A real isStartingPoint region (e.g. "Lobby") IS a walkable trigger
      // volume, so an empty incoming region there means the user genuinely
      // exited it and the UI should reset like any other region.
      const isSyntheticJoinTrigger =
        joinTriggerNameRef.current.toLowerCase() === 'landing';

      if (
        !incomingRegion &&
        isSyntheticJoinTrigger &&
        isLandingTrigger(currentPlayerName ?? '', joinTriggerNameRef.current)
      ) {
        globals.currentRegion = incomingRegion;
        return;
      }

      const currentScript = currentPlayerName
        ? scriptsRef.current.find((x) => {
            const triggers = Array.isArray(x.scriptTrigger)
              ? x.scriptTrigger
              : [x.scriptTrigger];
            return triggers.some((trigger) =>
              triggerMatchesRegion(trigger, currentPlayerName),
            );
          })
        : null;

      const wasAudioPlaying = isAudioPlaybackActiveRef.current;

      // bridgeRegionGaps: the user walked into an empty region while a
      // finish_step script is still talking. Leaving is not itself an
      // interruption — leave the script alone entirely (no stop, no deferred
      // stop) so it keeps running through its remaining steps exactly as if
      // the participant had stayed. It only stops here if something else
      // actually claims the participant's attention: a new scripted region
      // (the `if (scriptData)` branch above/below applies the normal
      // interruptMode policy against it), or an explicit user action (Skip/
      // Hide). Region tracking is still updated so those still resolve
      // correctly against the participant's real location.
      const isRunning =
        linearScriptPlayerRef.current?.getCurrentState().isRunning ?? false;
      const shouldBridgeGap =
        wasAudioPlaying &&
        isRunning &&
        currentScript?.bridgeRegionGaps === true &&
        currentScript?.interruptMode === 'finish_step';

      if (shouldBridgeGap) {
        log(
          '[region] no script — bridgeRegionGaps: leaving current script running to completion',
          {
            scriptId: currentScript?.scriptId,
            interruptMode: currentScript?.interruptMode,
          },
        );
        globals.currentRegion = incomingRegion;
        return;
      }

      if (linearScriptPlayerRef.current?.getCurrentState().isRunning) {
        linearScriptPlayerRef.current?.stop();
      }
      stopPlaybackRef.current();
      enqueuedScriptIdRef.current = null;
      globals.currentRegion = incomingRegion;

      if (
        !incomingRegion ||
        !wasAudioPlaying ||
        currentScript?.triggerType === 'ui_click'
      ) {
        log('[region] no script — resetting UI to default state');
        globals.activeScreenGlob = SCREENS.DEFAULT;
        setActiveScreen(SCREENS.DEFAULT);
        setNavigationHistory([SCREENS.DEFAULT]);
        setIsScriptCompleted(false);
        setIsScriptTransitioning(false);
        setIsScriptQuizActive(false);
        setIsDisplayUserFeedback(false);
        setCanUserSkip(false);
        setPendingScriptStart(null);

        // No script for this region — free-form Q&A can only offer something
        // if it's on AND there's knowledge to draw on: either a global item
        // (no mapName — belongs to every map) or one tagged for the region
        // just entered. Without either, the assistant has nothing to offer,
        // so close the panel instead of showing an empty default screen.
        const hasGlobalKnowledge = ragItemsRef.current.some(
          (item) => !item.mapName,
        );
        const hasTriggerKnowledge = ragItemsRef.current.some((item) =>
          item.tags.some((tag) => triggerMatchesRegion(tag, incomingRegion)),
        );

        if (
          !isAskMeAnythingEnabledRef.current ||
          !(hasGlobalKnowledge || hasTriggerKnowledge)
        ) {
          pendingAutoCloseTimeoutRef.current = setTimeout(() => {
            pendingAutoCloseTimeoutRef.current = null;
            setOpen(false);
          }, AUTO_CLOSE_GRACE_MS);
        }
      }
      return;
    }

    const { triggerType, interruptMode } = scriptData;
    const isPlayerRunning =
      linearScriptPlayerRef.current?.getCurrentState().isRunning ?? false;
    const currentPlayerName = isPlayerRunning
      ? linearScriptPlayerRef.current?.getCurrentState().name
      : undefined;
    const currentScriptData = currentPlayerName
      ? scriptsRef.current.find((x) => {
          const triggers = Array.isArray(x.scriptTrigger)
            ? x.scriptTrigger
            : [x.scriptTrigger];
          return triggers.some((trigger) =>
            triggerMatchesRegion(trigger, currentPlayerName),
          );
        })
      : null;

    // Shared by both the ui_click/custom_spot path below and the generic path
    // further down: waits for the CURRENT script's step to finish (audio
    // ended) before cutting over to the incoming script, instead of stopping
    // it mid-step. A ui_click/custom_spot script's own playbackPolicy is
    // hardcoded to 'always' by the editor (see AssistantScriptDialog's
    // useScriptSteps), so unlike the generic path, this is never gated on the
    // incoming script's policy — finish_step is respected unconditionally
    // for an incoming ui_click/custom_spot, since checking "is incoming
    // policy always" would otherwise always be true and never protect it.
    const deferForFinishStep = (incomingScriptData: IAssistantScript) => {
      const scriptId = incomingScriptData.scriptId;
      const logMeta = {
        scriptId,
        playbackPolicy: incomingScriptData.playbackPolicy,
        triggerType: incomingScriptData.triggerType,
        interruptMode: incomingScriptData.interruptMode,
      };
      if (!isAudioPlaybackActiveRef.current) {
        log(
          '[region] finish_step (current script) — audio already ended, starting enqueued script immediately',
          logMeta,
        );
        setIsScriptCompleted(false);
        setPendingScriptStart(null);
        setIsScriptTransitioning(true);
        setTimeout(() => {
          if (linearScriptPlayerRef.current?.getCurrentState().isRunning) {
            linearScriptPlayerRef.current?.stop();
          }
          stopPlaybackRef.current();
          setIsScriptTransitioning(false);
          startFlowRef.current('', { scriptId });
        }, 500);
        return;
      }
      log(
        '[region] finish_step (current script) — enqueueing incoming script to start after current step ends',
        logMeta,
      );
      enqueuedScriptIdRef.current = scriptId;
    };

    // Case 1: ui_click / custom_spot — never auto-start, show the button.
    // Mirrors the non_interruptible gate below: if the currently playing
    // script is non_interruptible, an incoming ui_click/custom_spot is
    // ignored entirely (no stop, no button) — same as area_entry — instead
    // of always surfacing its button and cutting off the current script.
    if (triggerType === 'ui_click' || triggerType === 'custom_spot') {
      if (currentScriptData?.interruptMode === 'non_interruptible') {
        log(
          '[region] non_interruptible (current script) — incoming ui_click/custom_spot will not start or enqueue:',
          scriptData.scriptId,
        );
        globals.currentRegion = incomingRegion;
        return;
      }

      if (currentScriptData?.interruptMode === 'finish_step') {
        deferForFinishStep(scriptData);
        globals.currentRegion = incomingRegion;
        return;
      }

      log(
        '[region] ui_click/custom_spot — stopping current script, setting pendingScriptStart',
        {
          scriptId: scriptData.scriptId,
          playbackPolicy: scriptData.playbackPolicy,
          triggerType,
          interruptMode,
        },
      );
      if (isPlayerRunning) {
        linearScriptPlayerRef.current?.stop();
      }
      stopPlaybackRef.current();
      enqueuedScriptIdRef.current = null;
      globals.currentRegion = incomingRegion;
      startFlowRef.current('', { scriptId: scriptData.scriptId });
      return;
    }

    if (isPlayerRunning) {
      if (currentScriptData?.interruptMode === 'non_interruptible') {
        log(
          '[region] non_interruptible (current script) — incoming script will not start or enqueue:',
          scriptData.scriptId,
        );
        globals.currentRegion = incomingRegion;
        return;
      }

      if (
        currentScriptData?.interruptMode === 'finish_step' &&
        effectivePlaybackPolicy !== PlaybackPolicy.ALWAYS
      ) {
        deferForFinishStep(scriptData);
        globals.currentRegion = incomingRegion;
        return;
      }
    }

    // Case 3: non_interruptible — do not interrupt the currently running script
    if (interruptMode === 'non_interruptible' && isPlayerRunning) {
      log(
        '[region] non_interruptible — current script is running, not interrupting. New region script will not start:',
        scriptData.scriptId,
      );
      globals.currentRegion = incomingRegion;
      return;
    }

    // Case 2: hard_cut (or area_entry with no running player) — stop immediately and start new script
    log('[region] hard_cut — stopping current script and starting new one', {
      scriptId: scriptData.scriptId,
      playbackPolicy: scriptData.playbackPolicy,
      triggerType,
      interruptMode,
    });
    if (isPlayerRunning) {
      linearScriptPlayerRef.current?.stop();
    }
    stopPlaybackRef.current();
    enqueuedScriptIdRef.current = null;
    globals.currentRegion = incomingRegion;
    startFlowRef.current('', { scriptId: scriptData.scriptId });
  }, []);

  const simulateRegionChange = useCallback((regionName: string | undefined) => {
    setSimulatedRegionOverride(regionName);
  }, []);

  return {
    simulatedRegionOverride,
    handleRegionChange,
    simulateRegionChange,
    cancelPendingAutoClose,
  };
};
